import api from './api'
import { PageResponse } from '@/types/comum'
import {
  EventoNotificacao,
  NotificacaoDetalhe,
  NotificacaoResumo,
} from '@/types/notificacao'

/**
 * Caixa de notificações do usuário do token (Onda 8 / A16) — `GET /v1/notificacoes`.
 *
 * Espelha o contrato REAL do `NotificacaoController` (módulo `originei-notificacoes`):
 * lista paginada de `NotificacaoResumoResponse` e detalhe `NotificacaoResponse`, sem
 * envelope `ApiResponse`. O `notificacaoService` antigo tipa a lista com o formato legado
 * (`titulo/mensagem/tipo/lida`), que o backend nunca devolveu — por isso o serviço novo;
 * o antigo segue servindo o sino da topbar (contagem) e as preferências de e-mail.
 *
 * NÃO usa `/v1/notificacoes/historico`: é a central do back-office (OPERADOR+), negada a
 * quem tem escopo de tenant.
 */
const BASE = '/api/v1/notificacoes'

export const notificacoesService = {
  /** Página da caixa do usuário, mais recentes primeiro (ordenação do backend). */
  listar: async (page = 0, size = 20): Promise<PageResponse<NotificacaoResumo>> =>
    (await api.get<PageResponse<NotificacaoResumo>>(`${BASE}?page=${page}&size=${size}`)) as unknown as PageResponse<NotificacaoResumo>,

  /** Detalhe (com o conteúdo) — só do próprio destinatário; o backend responde 403/404 senão. */
  detalhe: async (id: string): Promise<NotificacaoDetalhe> =>
    (await api.get<NotificacaoDetalhe>(`${BASE}/${id}`)) as unknown as NotificacaoDetalhe,

  marcarLida: async (id: string): Promise<void> => {
    await api.put(`${BASE}/${id}/lida`)
  },

  marcarTodasLidas: async (): Promise<void> => {
    await api.put(`${BASE}/todas-lidas`)
  },

  /** Total de não lidas do usuário (mesma fonte do sino) — o backend devolve um Long puro. */
  contarNaoLidas: async (): Promise<number> => {
    const count = (await api.get<number>(`${BASE}/nao-lidas/count`)) as unknown as number
    return Number(count ?? 0)
  },
}

/** Ação sugerida por tipo de evento: rota do Figma + rótulo do botão. */
export interface CtaNotificacao {
  rota: string
  rotulo: string
}

/**
 * CTA por `TipoEvento` (definição de pronto do A16). Só entra aqui evento que tem uma
 * tela do consultor para onde levar; os demais (LEMBRETE_ACAO genérico, SIMULACAO_CRIADA
 * com o simulador fora do ar — `features.ts`) ficam sem CTA de propósito, em vez de
 * mandar para o dashboard.
 */
export const CTA_POR_EVENTO: Partial<Record<EventoNotificacao, CtaNotificacao>> = {
  PENDENCIA_CRIADA: { rota: '/pendencias-documentos', rotulo: 'Abrir Pendências e Documentos' },
  DOCUMENTO_ANALISADO: { rota: '/pendencias-documentos?aba=documentos', rotulo: 'Abrir Documentos' },
  DOCUMENTO_PROCESSADO: { rota: '/pendencias-documentos?aba=documentos', rotulo: 'Abrir Documentos' },
  OPORTUNIDADE_CRIADA: { rota: '/reservas', rotulo: 'Abrir Minhas Reservas' },
  ESTAGIO_ATUALIZADO: { rota: '/reservas', rotulo: 'Abrir Minhas Reservas' },
  RESERVA_VENCENDO: { rota: '/reservas', rotulo: 'Abrir Minhas Reservas' },
  RESERVA_TRANSFERIDA: { rota: '/reservas', rotulo: 'Abrir Minhas Reservas' },
  RESERVA_ENVIADA_ANALISE: { rota: '/reservas', rotulo: 'Abrir Minhas Reservas' },
  RENOVACAO_ANALISADA: { rota: '/reservas?aba=renovacoes', rotulo: 'Abrir Renovações' },
  OPORTUNIDADE_CONTRATADA: { rota: '/operacoes-clientes', rotulo: 'Abrir Operações e Clientes' },
  CLIENTE_CRIADO: { rota: '/operacoes-clientes?aba=clientes', rotulo: 'Abrir Clientes' },
  CLIENTE_ATUALIZADO: { rota: '/operacoes-clientes?aba=clientes', rotulo: 'Abrir Clientes' },
  LEMBRETE_AGENDAMENTO: { rota: '/agenda', rotulo: 'Abrir Agenda' },
}

/**
 * Para o consultor, Operações e Clientes é tela do back-office (23/10): a operação
 * contratada abre Minhas Reservas e os eventos de cliente ficam sem botão.
 * VC-09: a mesma rota de pendências se chama "Central de Pendências" para o consultor.
 */
const CTA_CONSULTOR: Partial<Record<EventoNotificacao, CtaNotificacao | null>> = {
  PENDENCIA_CRIADA: { rota: '/pendencias-documentos', rotulo: 'Abrir Central de Pendências' },
  OPORTUNIDADE_CONTRATADA: { rota: '/reservas', rotulo: 'Abrir Minhas Reservas' },
  CLIENTE_CRIADO: null,
  CLIENTE_ATUALIZADO: null,
}

export const ctaDoEvento = (
  evento: EventoNotificacao | string,
  consultor = false,
): CtaNotificacao | null => {
  const e = evento as EventoNotificacao
  if (consultor && e in CTA_CONSULTOR) return CTA_CONSULTOR[e] ?? null
  return CTA_POR_EVENTO[e] ?? null
}

/** Rótulo humano do evento (chip da lista / detalhe). Desconhecido cai no próprio código. */
export const ROTULO_EVENTO: Partial<Record<EventoNotificacao, string>> = {
  DOCUMENTO_ANALISADO: 'Documento analisado',
  DOCUMENTO_PROCESSADO: 'Documento processado',
  CLIENTE_CRIADO: 'Cliente criado',
  CLIENTE_ATUALIZADO: 'Cliente atualizado',
  OPORTUNIDADE_CRIADA: 'Reserva criada',
  ESTAGIO_ATUALIZADO: 'Etapa atualizada',
  OPORTUNIDADE_CONTRATADA: 'Operação contratada',
  SIMULACAO_CRIADA: 'Simulação',
  LEMBRETE_ACAO: 'Lembrete',
  PENDENCIA_CRIADA: 'Pendência',
  RESERVA_VENCENDO: 'Reserva vencendo',
  RENOVACAO_ANALISADA: 'Renovação analisada',
  RESERVA_TRANSFERIDA: 'Reserva transferida',
  RESERVA_ENVIADA_ANALISE: 'Reserva em análise',
  LEMBRETE_AGENDAMENTO: 'Agendar reunião',
}

export const rotuloDoEvento = (evento: EventoNotificacao | string): string =>
  ROTULO_EVENTO[evento as EventoNotificacao] ?? String(evento)
